import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Coins,
  Package,
  RefreshCw,
  Search,
  Settings,
  Tag,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@components/Card';
import { Badge } from '@components/Card';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import { SimpleTable, TableCell, TableHeader, TableRow } from '@components/Table';
import { usePermissions } from '@hooks/usePermissions';
import { stockAnalyticsService } from '../../services/stockAnalyticsService';
import {
  ApplySuggestionItem,
  PaginationInfo,
  ProductHistoryResponse,
  SLOW_MOVING_LABELS,
  StockAnalyticsSummary,
  StockMetricsRow,
} from '../../types/stockAnalytics';
import { MetricInfoTooltip } from './MetricInfoTooltip';
import { StockAnalyticsParametersModal } from './StockAnalyticsParametersModal';
import { StockAnalyticsConceptsView } from './StockAnalyticsConceptsView';
import { RationalePanel } from './RationalePanel';
import { alignMonthlySeries } from './stockAnalyticsUtils';

// ─── Formatting helpers ─────────────────────────────────────────

const fmtInt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString('pt-BR'));
const fmtDec = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { maximumFractionDigits: digits, minimumFractionDigits: 0 });
const fmtCurrency = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDateTime = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const HISTORY_MONTHS = 24;

const slowMovingVariant = (cls: number): 'success' | 'warning' | 'danger' | 'neutral' => {
  switch (cls) {
    case 0:
      return 'success';
    case 1:
      return 'warning';
    case 2:
    case 3:
      return 'danger';
    default:
      return 'neutral';
  }
};

// ─── Sub-components ─────────────────────────────────────────────

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  accent: string;
  infoKey?: string;
}> = ({ icon, label, value, sub, accent, infoKey }) => (
  <Card padding="sm">
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-lg ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 flex items-center gap-1">
          {label}
          {infoKey && <MetricInfoTooltip metricKey={infoKey} />}
        </p>
        <p className="text-xl font-bold text-slate-900 truncate">{value}</p>
        {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
      </div>
    </div>
  </Card>
);

/** Expansão da linha: vendas 24m, histórico 24m (demanda × estoque médio), índices sazonais e racional. */
const ProductExpansion: React.FC<{ row: StockMetricsRow }> = ({ row }) => {
  const [history, setHistory] = useState<ProductHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    stockAnalyticsService
      .getProductHistory(row.product_id)
      .then((h) => !cancelled && setHistory(h))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Erro ao carregar histórico'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [row.product_id]);

  // Janela fixa dos últimos 24 meses (meses sem linha entram como zero): o mês
  // é lido do texto ISO — new Date('…-01T00:00:00Z').getMonth() recua um mês em UTC−3.
  const aligned = useMemo(() => alignMonthlySeries(history?.monthly, HISTORY_MONTHS, new Date()), [history]);

  const chartData = useMemo(
    () =>
      aligned.map((p) => ({
        name: p.label,
        'Demanda líquida': p.row?.qty_net ?? 0,
        'Estoque médio': p.row?.avg_stock_qty != null ? Math.round(p.row.avg_stock_qty * 100) / 100 : null,
      })),
    [aligned],
  );

  const salesData = useMemo(
    () =>
      aligned.map((p) => ({
        name: p.label,
        Vendida: p.row?.qty_sold ?? 0,
        Devolvida: p.row?.qty_returned ?? 0,
        Pedidos: p.row?.orders_count ?? 0,
      })),
    [aligned],
  );
  const soldTotal = useMemo(() => salesData.reduce((acc, m) => acc + m.Vendida, 0), [salesData]);

  const seasonalData = useMemo(() => {
    const indexes = history?.seasonal_indexes;
    if (!indexes || indexes.length !== 12) return null;
    return indexes.map((v, i) => ({ name: MONTH_LABELS[i], Índice: Math.round(v * 100) / 100 }));
  }, [history]);

  if (loading) return <p className="text-sm text-slate-500 py-4">Carregando histórico…</p>;
  if (error) return <p className="text-sm text-red-600 py-4">{error}</p>;

  return (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
            Quantidade vendida por mês (24 meses)
            <MetricInfoTooltip metricKey="qty_sold_monthly" />
            <span className="ml-auto font-normal text-slate-500">{fmtInt(soldTotal)} un no período</span>
          </p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
                <YAxis yAxisId="qty" tick={{ fontSize: 10, fill: '#64748b' }} allowDecimals={false} />
                <YAxis yAxisId="orders" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="qty" dataKey="Vendida" fill="#059669" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="qty" dataKey="Devolvida" fill="#f43f5e" radius={[3, 3, 0, 0]} />
                <Line yAxisId="orders" type="monotone" dataKey="Pedidos" stroke="#94a3b8" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
            Índices sazonais
            <MetricInfoTooltip metricKey="seasonal_index" />
          </p>
          {seasonalData ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={seasonalData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#64748b' }} interval={0} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} domain={[0, 'auto']} />
                  <Tooltip />
                  <Bar dataKey="Índice" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-xs text-slate-400 py-8 text-center">
              Sem índices sazonais — histórico insuficiente (mínimo configurável, padrão 24 meses).
            </p>
          )}
        </div>
        <div className="xl:col-span-3 bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
            Demanda líquida × estoque médio (24 meses)
            <MetricInfoTooltip metricKey="avg_daily_demand" />
          </p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Demanda líquida" fill="#10b981" radius={[3, 3, 0, 0]} />
                <Line type="monotone" dataKey="Estoque médio" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-700 mb-2">Como foi calculado</p>
        <RationalePanel rationaleJson={row.rationale_json} />
      </div>
    </div>
  );
};

// ─── Main view ──────────────────────────────────────────────────

type AnalyticsSubTab = 'indicadores' | 'conceitos';

export const StockAnalyticsReportView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canApply = checkPermission('stock_analytics.apply_suggestions').hasPermission;
  const canManageParams = checkPermission('stock_analytics.manage_parameters').hasPermission;
  const [subTab, setSubTab] = useState<AnalyticsSubTab>('indicadores');

  const [summary, setSummary] = useState<StockAnalyticsSummary | null>(null);
  const [rows, setRows] = useState<StockMetricsRow[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  // filtros
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [abcClass, setAbcClass] = useState('');
  const [xyzClass, setXyzClass] = useState('');
  const [slowClass, setSlowClass] = useState('');
  const [excessOnly, setExcessOnly] = useState(false);
  const [riskOnly, setRiskOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('sku');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [applyItems, setApplyItems] = useState<ApplySuggestionItem[] | null>(null);
  const [applying, setApplying] = useState(false);
  const [paramsOpen, setParamsOpen] = useState(false);

  const loadSummary = useCallback(() => {
    stockAnalyticsService.getSummary().then(setSummary).catch(() => setSummary(null));
  }, []);

  const loadRows = useCallback(() => {
    setLoading(true);
    setError(null);
    stockAnalyticsService
      .getMetrics({
        page,
        limit: 50,
        search,
        abc_class: abcClass || undefined,
        xyz_class: xyzClass || undefined,
        slow_moving_class: slowClass === '' ? null : Number(slowClass),
        excess_only: excessOnly,
        stockout_risk_only: riskOnly,
        sort_by: sortBy,
        sort_dir: sortDir,
      })
      .then(({ rows: data, pagination: p }) => {
        setRows(data);
        setPagination(p);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar análise de estoque'))
      .finally(() => setLoading(false));
  }, [page, search, abcClass, xyzClass, slowClass, excessOnly, riskOnly, sortBy, sortDir]);

  useEffect(loadSummary, [loadSummary]);
  useEffect(loadRows, [loadRows]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(1);
  };

  const selectableRows = rows.filter((r) => r.suggested_min_qty !== null && r.suggested_max_qty !== null);
  const selectedCount = selectableRows.filter((r) => selected[r.product_id]).length;

  const toggleSelectAll = () => {
    const allSelected = selectedCount === selectableRows.length && selectableRows.length > 0;
    const next: Record<number, boolean> = { ...selected };
    selectableRows.forEach((r) => {
      next[r.product_id] = !allSelected;
    });
    setSelected(next);
  };

  const openApplyModal = (items: ApplySuggestionItem[]) => {
    setFeedback(null);
    setApplyItems(items);
  };

  const buildItem = (row: StockMetricsRow): ApplySuggestionItem => ({
    product_id: row.product_id,
    min_stock_quantity: row.suggested_min_qty ?? 0,
    max_stock_quantity: row.suggested_max_qty ?? 0,
  });

  const handleConfirmApply = async () => {
    if (!applyItems) return;
    setApplying(true);
    try {
      const result = await stockAnalyticsService.applySuggestions(applyItems);
      setFeedback({
        kind: result.failed === 0 ? 'success' : 'error',
        message:
          result.failed === 0
            ? `Mín/Máx aplicados em ${result.applied} produto(s).`
            : `${result.applied} aplicado(s), ${result.failed} falha(s): ${result.results
                .filter((r) => !r.success)
                .map((r) => `#${r.product_id} (${r.error ?? 'erro'})`)
                .join(', ')}`,
      });
      setSelected({});
      setApplyItems(null);
      loadRows();
    } catch (err) {
      setFeedback({ kind: 'error', message: err instanceof Error ? err.message : 'Erro ao aplicar sugestões' });
    } finally {
      setApplying(false);
    }
  };

  const applyModalRows = useMemo(() => {
    if (!applyItems) return [];
    const byId = new Map(rows.map((r) => [r.product_id, r]));
    return applyItems.map((item) => ({ item, row: byId.get(item.product_id) }));
  }, [applyItems, rows]);

  const abcChartData = useMemo(() => {
    if (!summary) return [];
    return ['A', 'B', 'C'].map((c) => ({ name: `Classe ${c}`, Produtos: summary.abc_counts?.[c] ?? 0 }));
  }, [summary]);

  const agingChartData = useMemo(() => {
    if (!summary) return [];
    return [
      { name: 'Normal', Produtos: summary.products_analyzed - summary.slow_moving_count - summary.no_history_count },
      { name: 'Baixo giro', Produtos: summary.slow_moving_count - summary.idle_count },
      { name: 'Parado', Produtos: summary.idle_count },
      { name: 'Sem histórico', Produtos: summary.no_history_count },
    ];
  }, [summary]);

  const sortIndicator = (column: string) => (sortBy === column ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  return (
    <div className="space-y-6">
      {/* Sub-abas: Indicadores | Conceitos */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {([
          { id: 'indicadores' as const, label: 'Indicadores' },
          { id: 'conceitos' as const, label: 'Conceitos' },
        ]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={
              subTab === tab.id
                ? 'px-4 py-1.5 text-sm font-medium rounded-md bg-white text-emerald-700 shadow-sm'
                : 'px-4 py-1.5 text-sm font-medium rounded-md text-slate-500 hover:text-slate-700'
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subTab === 'conceitos' && <StockAnalyticsConceptsView />}

      {subTab === 'indicadores' && (
      <div className="space-y-6">
      {/* Cabeçalho da aba + ações */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500">
          Indicadores calculados diariamente pelo processo batch <span className="font-mono text-xs">stock_analytics</span>.
          Última atualização: <span className="font-medium text-slate-700">{fmtDateTime(summary?.last_calculated_at)}</span>
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { loadSummary(); loadRows(); }}>
            <RefreshCw size={14} className="mr-1.5" /> Atualizar
          </Button>
          {canManageParams && (
            <Button variant="secondary" onClick={() => setParamsOpen(true)}>
              <Settings size={14} className="mr-1.5" /> Parâmetros
            </Button>
          )}
        </div>
      </div>

      {feedback && (
        <div
          className={
            feedback.kind === 'success'
              ? 'px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-center gap-2'
              : 'px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-center gap-2'
          }
        >
          {feedback.kind === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {feedback.message}
        </div>
      )}

      {/* Cards de resumo */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-3">
          <SummaryCard
            icon={<Package size={18} className="text-emerald-700" />}
            accent="bg-emerald-100"
            label="Produtos analisados"
            value={fmtInt(summary.products_analyzed)}
          />
          <SummaryCard
            icon={<AlertTriangle size={18} className="text-red-700" />}
            accent="bg-red-100"
            label="Risco de ruptura"
            value={fmtInt(summary.stockout_risk_count)}
            infoKey="stockout_risk"
          />
          <SummaryCard
            icon={<Clock size={18} className="text-amber-700" />}
            accent="bg-amber-100"
            label="Baixo giro / parados"
            value={fmtInt(summary.slow_moving_count)}
            sub={`${fmtInt(summary.idle_count)} parados`}
            infoKey="slow_moving_class"
          />
          <SummaryCard
            icon={<Wallet size={18} className="text-orange-700" />}
            accent="bg-orange-100"
            label="Capital em excesso"
            value={fmtCurrency(summary.excess_value_total)}
            sub={`${fmtInt(summary.excess_count)} produtos`}
            infoKey="excess_stock"
          />
          <SummaryCard
            icon={<Coins size={18} className="text-teal-700" />}
            accent="bg-teal-100"
            label="Custo total do estoque"
            value={fmtCurrency(summary.stock_purchase_cost_total)}
            sub="preço de compra do fornecedor"
            infoKey="stock_purchase_cost_total"
          />
          <SummaryCard
            icon={<Tag size={18} className="text-indigo-700" />}
            accent="bg-indigo-100"
            label="Valor de venda do estoque"
            value={fmtCurrency(summary.stock_sale_value_total)}
            sub="preço de venda dos produtos"
            infoKey="stock_sale_value_total"
          />
          <SummaryCard
            icon={<TrendingUp size={18} className="text-blue-700" />}
            accent="bg-blue-100"
            label="Giro médio (12m)"
            value={fmtDec(summary.avg_turnover_365d, 2)}
            infoKey="turnover_365d"
          />
          <SummaryCard
            icon={<Archive size={18} className="text-slate-700" />}
            accent="bg-slate-100"
            label="Sem histórico"
            value={fmtInt(summary.no_history_count)}
            sub="produtos nunca vendidos"
          />
        </div>
      )}

      {/* Distribuições */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card padding="sm">
            <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              Distribuição ABC (receita 12m)
              <MetricInfoTooltip metricKey="abc_class" />
            </p>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={abcChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="Produtos" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card padding="sm">
            <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              Aging do estoque (tempo sem venda)
              <MetricInfoTooltip metricKey="days_since_last_sale" />
            </p>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agingChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="Produtos" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-slate-600 mb-1">Buscar (SKU ou descrição)</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearch(searchInput.trim());
                    setPage(1);
                  }
                }}
                placeholder="Digite e pressione Enter"
                className="input-field w-full pl-9"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Classe ABC</label>
            <select value={abcClass} onChange={(e) => { setAbcClass(e.target.value); setPage(1); }} className="input-field">
              <option value="">Todas</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Classe XYZ</label>
            <select value={xyzClass} onChange={(e) => { setXyzClass(e.target.value); setPage(1); }} className="input-field">
              <option value="">Todas</option>
              <option value="X">X (estável)</option>
              <option value="Y">Y (variável)</option>
              <option value="Z">Z (errática)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Giro</label>
            <select value={slowClass} onChange={(e) => { setSlowClass(e.target.value); setPage(1); }} className="input-field">
              <option value="">Todos</option>
              {Object.entries(SLOW_MOVING_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 pb-2 cursor-pointer">
            <input type="checkbox" checked={riskOnly} onChange={(e) => { setRiskOnly(e.target.checked); setPage(1); }} className="rounded border-slate-300" />
            Só risco de ruptura
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600 pb-2 cursor-pointer">
            <input type="checkbox" checked={excessOnly} onChange={(e) => { setExcessOnly(e.target.checked); setPage(1); }} className="rounded border-slate-300" />
            Só excesso
          </label>
          {canApply && (
            <Button
              variant="primary"
              disabled={selectedCount === 0}
              onClick={() => openApplyModal(selectableRows.filter((r) => selected[r.product_id]).map(buildItem))}
            >
              Aplicar selecionados ({selectedCount})
            </Button>
          )}
        </div>
      </Card>

      {error && <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      {/* Tabela */}
      <SimpleTable>
        <TableHeader>
          <tr>
            {canApply && (
              <th className="table-header th w-8">
                <input
                  type="checkbox"
                  checked={selectableRows.length > 0 && selectedCount === selectableRows.length}
                  onChange={toggleSelectAll}
                  className="rounded border-slate-300"
                />
              </th>
            )}
            <th className="table-header th w-6"></th>
            <th className="table-header th cursor-pointer select-none" onClick={() => handleSort('sku')}>SKU{sortIndicator('sku')}</th>
            <th className="table-header th">Descrição</th>
            <th className="table-header th cursor-pointer select-none" onClick={() => handleSort('current_stock_qty')}>Físico{sortIndicator('current_stock_qty')}</th>
            <th className="table-header th cursor-pointer select-none" onClick={() => handleSort('current_stock_available_qty')}>
              <span className="inline-flex items-center gap-1">Disponível{sortIndicator('current_stock_available_qty')} <MetricInfoTooltip metricKey="current_stock_available_qty" /></span>
            </th>
            <th className="table-header th"><span className="inline-flex items-center gap-1">Em pedido <MetricInfoTooltip metricKey="on_order_qty" /></span></th>
            <th className="table-header th cursor-pointer select-none" onClick={() => handleSort('coverage_days')}>
              <span className="inline-flex items-center gap-1">Cobertura{sortIndicator('coverage_days')} <MetricInfoTooltip metricKey="coverage_days" /></span>
            </th>
            <th className="table-header th cursor-pointer select-none" onClick={() => handleSort('turnover_365d')}>
              <span className="inline-flex items-center gap-1">Giro 12m{sortIndicator('turnover_365d')} <MetricInfoTooltip metricKey="turnover_365d" /></span>
            </th>
            <th className="table-header th">
              <span className="inline-flex items-center gap-1">ABC/XYZ <MetricInfoTooltip metricKey="abc_class" /></span>
            </th>
            <th className="table-header th">
              <span className="inline-flex items-center gap-1">Situação <MetricInfoTooltip metricKey="slow_moving_class" /></span>
            </th>
            <th className="table-header th">
              <span className="inline-flex items-center gap-1">Mín/Máx atual → sugerido <MetricInfoTooltip metricKey="suggested_min_qty" /></span>
            </th>
            <th className="table-header th cursor-pointer select-none" onClick={() => handleSort('suggested_order_qty')}>
              <span className="inline-flex items-center gap-1">Compra sugerida{sortIndicator('suggested_order_qty')} <MetricInfoTooltip metricKey="suggested_order_qty" /></span>
            </th>
            {canApply && <th className="table-header th"></th>}
          </tr>
        </TableHeader>
        <tbody>
          {loading && (
            <TableRow>
              <TableCell colSpan={14} className="text-center py-8">
                <span className="text-sm text-slate-500">Carregando análise de estoque…</span>
              </TableCell>
            </TableRow>
          )}
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={14} className="text-center py-10">
                <span className="text-sm text-slate-500">
                  Nenhum produto encontrado. Se o processo batch ainda não rodou, execute-o na tela de Processos.
                </span>
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            rows.map((row) => {
              const hasSuggestion = row.suggested_min_qty !== null && row.suggested_max_qty !== null;
              const minChanged = hasSuggestion && row.suggested_min_qty !== (row.current_min_qty ?? 0);
              const maxChanged = hasSuggestion && row.suggested_max_qty !== (row.current_max_qty ?? 0);
              const expanded = expandedId === row.product_id;
              return (
                <React.Fragment key={row.product_id}>
                  <TableRow className="cursor-pointer">
                    {canApply && (
                      <TableCell>
                        <input
                          type="checkbox"
                          disabled={!hasSuggestion}
                          checked={!!selected[row.product_id] && hasSuggestion}
                          onChange={(e) => setSelected({ ...selected, [row.product_id]: e.target.checked })}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-slate-300 disabled:opacity-30"
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <button onClick={() => setExpandedId(expanded ? null : row.product_id)} className="text-slate-400 hover:text-emerald-600">
                        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{row.sku}</TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-800">{row.description ?? '—'}</span>
                      {row.provider_name && <span className="block text-[11px] text-slate-400">{row.provider_name}</span>}
                    </TableCell>
                    <TableCell>{fmtInt(row.current_stock_qty)}</TableCell>
                    <TableCell>
                      <span className={row.current_stock_available_qty !== row.current_stock_qty ? 'font-medium text-slate-800' : ''}>
                        {fmtInt(row.current_stock_available_qty)}
                      </span>
                      {row.current_stock_available_qty !== row.current_stock_qty && (
                        <span className="block text-[10px] text-slate-400">
                          {fmtInt(row.current_stock_qty - row.current_stock_available_qty)} reservado
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{fmtDec(row.on_order_qty, 0)}</TableCell>
                    <TableCell>
                      {row.coverage_days !== null ? (
                        <span className={row.stockout_risk_flag ? 'text-red-600 font-semibold' : row.excess_stock_flag ? 'text-orange-600 font-semibold' : ''}>
                          {fmtDec(row.coverage_days, 0)} d
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{fmtDec(row.turnover_365d, 2)}</TableCell>
                    <TableCell>
                      <span className="font-semibold text-slate-700">{row.abc_class ?? '—'}</span>
                      <span className="text-slate-400">/</span>
                      <span className="font-semibold text-slate-700">{row.xyz_class ?? '—'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant={slowMovingVariant(row.slow_moving_class)} size="sm">
                          {SLOW_MOVING_LABELS[row.slow_moving_class] ?? '?'}
                        </Badge>
                        {row.stockout_risk_flag && <Badge variant="danger" size="sm">Ruptura</Badge>}
                        {row.excess_stock_flag && <Badge variant="warning" size="sm">Excesso</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {hasSuggestion ? (
                        <span className="text-xs">
                          {fmtInt(row.current_min_qty)}/{fmtInt(row.current_max_qty)}
                          <span className="text-slate-400 mx-1">→</span>
                          <span className={minChanged ? 'font-bold text-emerald-700' : ''}>{fmtInt(row.suggested_min_qty)}</span>
                          /
                          <span className={maxChanged ? 'font-bold text-emerald-700' : ''}>{fmtInt(row.suggested_max_qty)}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">sem base estatística</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.suggested_order_qty !== null && row.suggested_order_qty > 0 ? (
                        <span className="font-semibold text-emerald-700">{fmtInt(row.suggested_order_qty)}</span>
                      ) : (
                        fmtInt(row.suggested_order_qty)
                      )}
                    </TableCell>
                    {canApply && (
                      <TableCell>
                        <Button
                          variant="outline"
                          disabled={!hasSuggestion || (!minChanged && !maxChanged)}
                          onClick={() => openApplyModal([buildItem(row)])}
                          title={!hasSuggestion ? 'Produto sem sugestão (sem histórico)' : undefined}
                        >
                          Aplicar
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                  {expanded && (
                    <tr className="bg-slate-50">
                      <td colSpan={14} className="px-6 py-3">
                        <ProductExpansion row={row} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </SimpleTable>

      {/* Paginação */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>
            Página {pagination.page} de {pagination.total_pages} — {fmtInt(pagination.total_items)} produtos
          </span>
          <div className="flex gap-2">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
            <Button variant="outline" disabled={page >= pagination.total_pages} onClick={() => setPage(page + 1)}>Próxima</Button>
          </div>
        </div>
      )}

      {/* Modal de confirmação de aplicação */}
      <Dialog
        isOpen={applyItems !== null}
        onClose={() => !applying && setApplyItems(null)}
        title="Aplicar estoque mínimo/máximo sugerido"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setApplyItems(null)} disabled={applying}>Cancelar</Button>
            <Button variant="primary" onClick={handleConfirmApply} disabled={applying}>
              {applying ? 'Aplicando…' : `Confirmar (${applyItems?.length ?? 0})`}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Os valores abaixo serão gravados no cadastro dos produtos (campos Estoque Mínimo e Estoque Máximo).
          </p>
          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
            {applyModalRows.map(({ item, row }) => (
              <div key={item.product_id} className="px-3 py-2 flex items-center justify-between text-sm">
                <span className="font-mono text-xs">{row?.sku ?? `#${item.product_id}`}</span>
                <span className="text-xs">
                  {fmtInt(row?.current_min_qty)}/{fmtInt(row?.current_max_qty)}
                  <span className="text-slate-400 mx-1">→</span>
                  <span className="font-semibold text-emerald-700">{item.min_stock_quantity}/{item.max_stock_quantity}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </Dialog>

      {/* Modal de parâmetros (admin) */}
      <StockAnalyticsParametersModal
        isOpen={paramsOpen}
        onClose={() => setParamsOpen(false)}
        onSaved={() => setFeedback({ kind: 'success', message: 'Parâmetros salvos. Valem a partir da próxima execução do batch.' })}
      />
      </div>
      )}
    </div>
  );
};

export default StockAnalyticsReportView;
